import { type PriceObjectDto } from "@/modules/common/priceObject/gateway/priceObjectApiDto"
import {
  type CreateUserProgramInputDto,
  type UpdateUserProgramInputDto,
} from "@/modules/programs/gateway/programInputDto"
import { type ListOrder } from "@/modules/utils/types"
import { USER_TEST_DATA } from "@modules/profile/publicProfile/test/publicProfileApi.fake"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { type ProgramApiGateway } from "@modules/programs/gateway/programApiGateway"
import { type UserProgramDetailsDto } from "@modules/programs/gateway/programDto"
import { type UserProgramItemDto } from "@modules/programs/gateway/userProgramItemDto"
import {
  ProgramFilter,
  ProgramStatus,
  ProgramSubscriptionStatus,
  ProgramType,
  ProgramVisibility,
} from "@modules/programs/types/programTypes"
import type { Page } from "@/modules/utils/paginated"

const FREE_INFINITE_PROGRAM: UserProgramItemDto = {
  id: "free_infinite_program",
  name: "Free Infinite Program",
  description: "Free Infinite Program Description",
  image: { url: "https://example.com/image.jpg", hash: "image" },
  owner: {
    ...USER_TEST_DATA.HEAD_COACH,
  },
  visibility: ProgramVisibility.PUBLIC,
  subscriptionCount: 0,
  subscribed: false,
  price: null,
  defaultHideSessionContent: false,
  status: ProgramStatus.PUBLISHED,
  subscriptionStatus: ProgramSubscriptionStatus.ACTIVE,
  duration: null,
  type: ProgramType.VIRTUAL,
  sharedCoachStatus: null,
  followedInBundle: false,
}

const PRICED_INFINITE_PROGRAM: UserProgramItemDto = {
  id: "priced_infinite_program",
  name: "Priced Infinite Program",
  description: "Priced Infinite Program Description",
  image: { url: "https://example.com/image.jpg", hash: "image" },
  owner: {
    ...USER_TEST_DATA.HEAD_COACH,
  },
  visibility: ProgramVisibility.PUBLIC,
  subscriptionCount: 0,
  subscribed: false,
  price: {
    value: 100,
    currency: "EUR",
  },
  defaultHideSessionContent: false,
  status: ProgramStatus.PUBLISHED,
  subscriptionStatus: ProgramSubscriptionStatus.ACTIVE,
  duration: null,
  type: ProgramType.VIRTUAL,
  sharedCoachStatus: null,
  followedInBundle: false,
}

export const PROGRAM_TEST_DATA = {
  FREE_INFINITE_PROGRAM,
  PRICED_INFINITE_PROGRAM,
}

export const LIGHT_PROGRAM_TEST_DATA = {
  FREE_INFINITE_PROGRAM: {
    id: "free_infinite_program",
    name: "Free Infinite Program",
    image: { url: "https://example.com/image.jpg", hash: "image" },
    price: null,
    type: ProgramType.VIRTUAL,
  },
  PRICED_INFINITE_PROGRAM: {
    id: "priced_infinite_program",
    name: "Priced Infinite Program",
    image: { url: "https://example.com/image.jpg", hash: "image" },
    price: { value: 100, currency: "EUR" },
    type: ProgramType.VIRTUAL,
  },
}

export const LIGHT_PROGRAM_BUNDLE_TEST_DATA = {
  PRICED_INFINITE_PROGRAM_BUNDLE: {
    id: "priced_infinite_program_bundle",
    name: "Priced Infinite Program Bundle",
    image: { url: "https://example.com/image.jpg", hash: "image" },
    price: { value: 100, currency: "EUR" },
    type: ProgramBundleType.VIRTUAL,
  },
}

const FREE_INFINITE_PROGRAM_DETAILS: UserProgramDetailsDto = {
  id: "free_infinite_program",
  name: "Free Infinite Program",
  description: "Free Infinite Program Description",
  image: { url: "https://example.com/image.jpg", hash: "image" },
  owner: {
    id: USER_TEST_DATA.HEAD_COACH.id,
    username: USER_TEST_DATA.HEAD_COACH.username,
    firstname: USER_TEST_DATA.HEAD_COACH.firstname,
    lastname: USER_TEST_DATA.HEAD_COACH.lastname,
    avatar: USER_TEST_DATA.HEAD_COACH.avatar,
    stripeAccountInError: false,
  },
  visibility: ProgramVisibility.PUBLIC,
  subscriptionStatus: ProgramSubscriptionStatus.ACTIVE,
  subscriptionCount: 0,
  price: null,
  entranceCheckEnabled: false,
  invitationLink: null,
  defaultHideSessionContent: false,
  status: ProgramStatus.PUBLISHED,
  duration: null,
  type: ProgramType.VIRTUAL,
  sessionCount: 0,
  trialDuration: null,
  scoreboardVisibility: true,
  programBundleSubscriptionId: null,
  sharedCoaches: null,
  subscribed: false,
  isEligibleToTrial: null,
  programBundle: null,
}

export const PROGRAM_DETAILS_TEST_DATA = {
  FREE_INFINITE_PROGRAM_DETAILS,
}

export class FakeProgramApi implements ProgramApiGateway {
  programs: UserProgramItemDto[] = []

  givenPrograms(programs: UserProgramItemDto[]) {
    this.programs = programs
  }

  getProgram(_: string): Promise<UserProgramDetailsDto> {
    return Promise.resolve(PROGRAM_DETAILS_TEST_DATA.FREE_INFINITE_PROGRAM_DETAILS)
  }
  getProgramForBox(_: string): Promise<UserProgramDetailsDto> {
    throw new Error("Method not implemented.")
  }
  getPrograms(
    _: ProgramFilter,
    _order: ListOrder,
    _page?: number,
    _size?: number,
    _onlyWithPrice?: boolean,
  ): Promise<Page<UserProgramItemDto>> {
    const programs = Object.values(this.programs)

    return Promise.resolve({
      page: 1,
      totalPages: 1,
      totalSize: programs.length,
      content: programs,
    })
  }

  updateProgramPrice(_: string, _price: PriceObjectDto, _trialDuration: number | null): Promise<UserProgramDetailsDto> {
    throw new Error("Method not implemented.")
  }

  removeProgramPrice(_: string): Promise<UserProgramDetailsDto> {
    throw new Error("Method not implemented.")
  }

  createProgram(_: CreateUserProgramInputDto): Promise<UserProgramDetailsDto> {
    throw new Error("Method not implemented.")
  }

  updateProgram(_: string, _programInput: UpdateUserProgramInputDto): Promise<UserProgramDetailsDto> {
    throw new Error("Method not implemented.")
  }

  deleteProgram(_: string): Promise<void> {
    throw new Error("Method not implemented.")
  }
}
